import { infringementAPI } from '@/utils/api';
import { convertImageToServerUrl } from '@/utils/imageUrlUtils';
import { mapInfringementRiskToListing } from '@/pages/ipe/workbench/utils/platformListingRiskAssessment';
import { pollInfringementBatch } from '@/pages/ipe/workbench/utils/infringementBatchPoll';
import { toProcessableImageUrl } from '@/pages/ipe/workbench/utils/sourcingWorkflowSpace';
import {
  listingRiskLabel,
  listingRiskStatusClass,
} from '@/pages/ipe/workbench/utils/resolveListingGalleryMeta';
import { resolvePublishSlotImageUrl } from '@/shein-platform/utils/sheinPublishImageSlots';

export const EMPTY_SHEIN_PRINT_INFRINGEMENT = {
  risk: 'pending',
  assessing: false,
  riskAdvice: '',
  assessedAt: '',
};

export function normalizeSheinPrintInfringement(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    risk: src.risk || 'pending',
    assessing: Boolean(src.assessing),
    riskAdvice: String(src.riskAdvice || ''),
    assessedAt: String(src.assessedAt || ''),
  };
}

export function resolveSheinPrintRiskMeta(infringement = EMPTY_SHEIN_PRINT_INFRINGEMENT) {
  const meta = normalizeSheinPrintInfringement(infringement);
  const hasRiskResult = Boolean(meta.riskAdvice)
    || (meta.risk && meta.risk !== 'pending' && !meta.assessing);
  return {
    meta,
    riskLabel: listingRiskLabel(meta.risk, meta.assessing),
    riskClass: listingRiskStatusClass(meta.risk, meta.assessing),
    canOpenRisk: hasRiskResult,
  };
}

export function sheinPrintRiskBadgeClass(risk, assessing = false) {
  if (assessing) return 'spo-infr-risk-badge--processing';
  if (risk === 'high') return 'spo-infr-risk-badge--high';
  if (risk === 'medium') return 'spo-infr-risk-badge--medium';
  if (risk === 'low') return 'spo-infr-risk-badge--low';
  return 'spo-infr-risk-badge--pending';
}

/** 首张印花图 URL（侵权检测用） */
export function resolveFirstPrintImageUrl(printImages = []) {
  const list = Array.isArray(printImages) ? printImages : [];
  for (const img of list) {
    const url = resolvePublishSlotImageUrl(img);
    if (url) return url;
  }
  return '';
}

async function resolveServerImageUrl(imageUrl) {
  const full = String(imageUrl || '').trim();
  if (!full) throw new Error('无法解析图片地址');
  if (full.startsWith('/uploads/')) return full;
  if (full.startsWith('http') && !full.startsWith('data:')) return full;
  const data = full.startsWith('data:') ? full : await toProcessableImageUrl(full);
  if (!data) throw new Error('无法读取图片');
  const server = await convertImageToServerUrl(data);
  if (!server) throw new Error('无法上传图片到服务器');
  return server;
}

/**
 * 对发品印花图执行深度侵权检测。
 * @returns {Promise<{ risk: string, riskAdvice: string, assessedAt: string, batchId?: string }>}
 */
export async function assessSheinPublishPrintInfringement(imageUrl, { remark = 'SHEIN发品印花侵权检测' } = {}) {
  const full = String(imageUrl || '').trim();
  if (!full) throw new Error('缺少可检测的印花图');

  const serverUrl = await resolveServerImageUrl(full);
  const created = await infringementAPI.createBatch({
    imageUrls: [serverUrl],
    mode: 'deep',
    remark,
  });
  const batchId = created?.id;
  if (!batchId) throw new Error('侵权检测任务创建失败');

  const detail = await pollInfringementBatch(batchId, {
    timeoutMessage: '侵权风险评估超时',
  });
  const items = Array.isArray(detail?.items) ? detail.items : [];
  const top = items[0];

  return {
    risk: mapInfringementRiskToListing(top?.riskLevel),
    riskAdvice: top?.advice || '',
    assessedAt: new Date().toISOString(),
    batchId,
  };
}
